import React, { memo, useEffect, useState, useMemo } from 'react';
import { Text, View, TouchableOpacity, ScrollView } from 'react-native';
import usePlatform from '@/utils/hooks/usePlatform';
import Input from '@/components/General/inputs/Input';
import SelectInput from '@/components/General/inputs/SelectInput';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import InputSection from '@/components/General/inputs/InputSection';
import ToggleInput from '@/components/General/inputs/ToggleInput';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { DateTime } from 'luxon';
import { StyleSheet } from 'react-native-unistyles';
import { RRule } from 'rrule';
import { 
    buildRRule, 
    parseRRuleToSettings, 
    getUpcomingDates, 
    getRecurrenceDescription,
    RECURRENCE_PRESETS,
    WEEKDAY_LABELS,
    RRULE_WEEKDAY_LABELS
} from '@/utils/recurrence';

const FREQUENCY_OPTIONS = ['Daily', 'Weekly', 'Monthly', 'Yearly'];
const WEEK_POSITIONS = ['1st', '2nd', '3rd', '4th', 'Last'];
const MONTH_OPTIONS = [
    { label: 'Jan', value: 1 }, { label: 'Feb', value: 2 }, { label: 'Mar', value: 3 },
    { label: 'Apr', value: 4 }, { label: 'May', value: 5 }, { label: 'Jun', value: 6 },
    { label: 'Jul', value: 7 }, { label: 'Aug', value: 8 }, { label: 'Sep', value: 9 },
    { label: 'Oct', value: 10 }, { label: 'Nov', value: 11 }, { label: 'Dec', value: 12 }
];

const DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => ({ label: i + 1, value: i + 1 }));

const RecurrenceSettingsNew = ({ isShowing, eventDetails, setEventDetails }) => {
    const [selectedPreset, setSelectedPreset] = useState(null);
    const [upcomingProjections, setUpcomingProjections] = useState([]);
    
    // Custom recurrence settings state
    const [recurrenceSettings, setRecurrenceSettings] = useState({
        frequency: 'Weekly',
        interval: 1,
        weekdays: [],
        monthDays: [],
        months: [],
        monthlyType: 'date', // 'date' or 'weekday'
        weekPosition: '1st',
        weekday: 'Sunday',
        endDate: null
    });

    const heightAnim = useSharedValue(0);
    const animationStyle = useAnimatedStyle(() => ({
        height: withTiming(heightAnim.value, { duration: 300 })
    }));

    // Calculate dynamic height based on visible sections
    useEffect(() => {
        if (isShowing) {
            let height = 280; // Base height for presets
            
            height += 70; // Frequency and interval

            if (recurrenceSettings.frequency === 'Weekly') {
                height += 100; // Weekday picker
            } else if (recurrenceSettings.frequency === 'Daily') {
                height += 15; // Monthly options
            } else if (recurrenceSettings.frequency === 'Monthly') {
                height += 305 // Monthly options
            } else if (recurrenceSettings.frequency === 'Yearly') {
                height += 460; // Month picker + day options
            }
            
            if (upcomingProjections.length > 0) {
                height += 120 + (upcomingProjections.length * 28);
            }
            
            heightAnim.value = height;
        } else {
            heightAnim.value = 0;
        }
    }, [isShowing, recurrenceSettings.frequency, upcomingProjections.length]);

    // Initialize from existing rruleString or legacy fields
    useEffect(() => {
        if (eventDetails.rruleString) {
            const parsed = parseRRuleToSettings(eventDetails.rruleString);
            if (parsed) {
                setRecurrenceSettings(prev => ({ ...prev, ...parsed }));
            }
        } else if (eventDetails.recurringFrequency) {
            // Legacy migration - convert old fields to new format
            migrateFromLegacy();
        }
    }, []);

    const migrateFromLegacy = () => {
        const settings = {
            frequency: eventDetails.recurringFrequency === 'Days' ? 'Daily' :
                       eventDetails.recurringFrequency === 'Weeks' ? 'Weekly' :
                       eventDetails.recurringFrequency === 'Months' ? 'Monthly' :
                       eventDetails.recurringFrequency === 'Years' ? 'Yearly' : 'Weekly',
            interval: parseInt(eventDetails.recurringInterval) || 1,
            weekdays: eventDetails.recurrenceDaysOfWeek || [],
            monthDays: eventDetails.recurrenceDaysOfMonth?.map(d => parseInt(d)) || [],
            months: eventDetails.recurrenceMonths?.map(m => {
                const monthMap = { 'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6, 
                                  'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12 };
                return monthMap[m] || 1;
            }) || [],
            monthlyType: eventDetails.recurrenceWeeksOfMonth?.length > 0 ? 'weekday' : 'date',
            weekPosition: eventDetails.recurrenceWeeksOfMonth?.[0] || '1st',
            weekday: eventDetails.recurrenceDaysOfWeek?.[0] || 'Sunday',
            endDate: eventDetails.recurrenceEndDate
        };
        
        setRecurrenceSettings(settings);
        updateRRuleString(settings);
    };

    // Update rruleString whenever settings change
    const updateRRuleString = (settings) => {
        if (!eventDetails.defaultStartTime) return;
        
        try {
            const rule = buildRRule(settings, new Date(eventDetails.defaultStartTime));
            const rruleString = rule.toString();
            
            setEventDetails({
                ...eventDetails,
                rruleString,
                recurrenceEndDate: settings.endDate
            });
        } catch (e) {
            console.error('Failed to build RRule:', e);
        }
    };

    // Keep rruleString dtstart in sync when the event time changes (so backend
    // instance generation uses the correct time-of-day and avoids duplicates).
    useEffect(() => {
        if (!eventDetails.isRecurring || !eventDetails.rruleString || !eventDetails.defaultStartTime) return;

        try {
            const rule = RRule.fromString(eventDetails.rruleString);
            const currentDtstart = new Date(rule.origOptions.dtstart);
            const newDtstart = new Date(eventDetails.defaultStartTime);

            if (currentDtstart.getTime() !== newDtstart.getTime()) {
                const newRule = new RRule({
                    ...rule.origOptions,
                    dtstart: newDtstart
                });
                setEventDetails({
                    ...eventDetails,
                    rruleString: newRule.toString()
                });
            }
        } catch (e) {
            console.error('Failed to update RRule dtstart:', e);
        }
    }, [eventDetails.defaultStartTime]);

    // Generate preview dates
    useEffect(() => {
        if (!eventDetails.rruleString || !eventDetails.defaultStartTime) {
            setUpcomingProjections([]);
            return;
        }

        try {
            const dates = getUpcomingDates(eventDetails.rruleString, 5, new Date(eventDetails.defaultStartTime));
            setUpcomingProjections(dates);
        } catch (e) {
            console.error('Failed to get upcoming dates:', e);
            setUpcomingProjections([]);
        }
    }, [eventDetails.rruleString, eventDetails.defaultStartTime]);

    const handlePresetSelect = (preset) => {
        setSelectedPreset(preset.id);
        
        if (!eventDetails.defaultStartTime) return;
        
        const startDate = new Date(eventDetails.defaultStartTime);
        const ruleOptions = preset.getRule(startDate);
        
        // Map preset rule options to recurrenceSettings state
        const frequencyMap = {
            [RRule.DAILY]: 'Daily',
            [RRule.WEEKLY]: 'Weekly',
            [RRule.MONTHLY]: 'Monthly',
            [RRule.YEARLY]: 'Yearly'
        };
        
        const newSettings = {
            frequency: frequencyMap[ruleOptions.freq] || 'Weekly',
            interval: ruleOptions.interval || 1,
            weekdays: [],
            monthDays: [],
            months: [],
            monthlyType: 'date',
            weekPosition: '1st',
            weekday: WEEKDAY_LABELS[startDate.getDay()],
            endDate: recurrenceSettings.endDate
        };
        
        if (ruleOptions.byweekday && Array.isArray(ruleOptions.byweekday)) {
            const days = ruleOptions.byweekday.map(wd => {
                if (typeof wd === 'number') {
                    return RRULE_WEEKDAY_LABELS[wd];
                } else if (wd.weekday !== undefined) {
                    return RRULE_WEEKDAY_LABELS[wd.weekday];
                }
                return null;
            }).filter(Boolean);
            newSettings.weekdays = days;
        }
        
        if (ruleOptions.bymonthday && Array.isArray(ruleOptions.bymonthday)) {
            newSettings.monthDays = ruleOptions.bymonthday;
        }
        
        if (ruleOptions.bymonth && Array.isArray(ruleOptions.bymonth)) {
            newSettings.months = ruleOptions.bymonth;
        }
        
        // For yearly preset, default to current month and day from start date
        if (newSettings.frequency === 'Yearly') {
            if (newSettings.months.length === 0) {
                newSettings.months = [startDate.getMonth() + 1];
            }
            if (newSettings.monthDays.length === 0) {
                newSettings.monthDays = [startDate.getDate()];
            }
        }
        
        // Handle nth weekday (e.g., 2nd Tuesday) used by monthly_weekday preset
        if (ruleOptions.byweekday && Array.isArray(ruleOptions.byweekday)) {
            const nthWeekday = ruleOptions.byweekday.find(wd => wd && typeof wd === 'object' && wd.n);
            if (nthWeekday) {
                newSettings.monthlyType = 'weekday';
                newSettings.weekday = RRULE_WEEKDAY_LABELS[nthWeekday.weekday];
                // Map week position: 1→'1st', 2→'2nd', 3→'3rd', 4→'4th', -1→'Last', >=5→'Last'
                const positionMap = { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th', '-1': 'Last' };
                const posKey = String(nthWeekday.n);
                newSettings.weekPosition = positionMap[posKey] || 'Last';
            }
        }
        
        setRecurrenceSettings(newSettings);
        updateRRuleString(newSettings);
    };

    const handleSettingChange = (key, value) => {
        const newSettings = { ...recurrenceSettings, [key]: value };
        setRecurrenceSettings(newSettings);
        updateRRuleString(newSettings);
    };

    const toggleWeekday = (day) => {
        const current = recurrenceSettings.weekdays || [];
        const newWeekdays = current.includes(day)
            ? current.filter(d => d !== day)
            : [...current, day];
        handleSettingChange('weekdays', newWeekdays);
    };

    const toggleMonthDay = (day) => {
        const current = recurrenceSettings.monthDays || [];
        const newDays = current.includes(day)
            ? current.filter(d => d !== day)
            : [...current, day];
        handleSettingChange('monthDays', newDays);
    };

    const toggleMonth = (month) => {
        const current = recurrenceSettings.months || [];
        const newMonths = current.includes(month)
            ? current.filter(m => m !== month)
            : [...current, month];
        handleSettingChange('months', newMonths);
    };

    const { isNative } = usePlatform();

    const recurrenceDescription = useMemo(() => {
        if (!eventDetails.rruleString) return '';
        return getRecurrenceDescription(eventDetails.rruleString);
    }, [eventDetails.rruleString]);

    const presetButtons = RECURRENCE_PRESETS.filter(p => p.id !== 'custom' && p.id !== 'biweekly').map((preset) => (
        <TouchableOpacity
            key={preset.id}
            style={[
                styles.presetButton,
                selectedPreset === preset.id && styles.presetButtonSelected
            ]}
            onPress={() => handlePresetSelect(preset)}
        >
            <Text style={[
                styles.presetButtonText,
                selectedPreset === preset.id && styles.presetButtonTextSelected
            ]}>
                {preset.label}
            </Text>
        </TouchableOpacity>
    ));

    const projectionItems = upcomingProjections.map((date, index) => (
        <View key={`projection_${index}`} style={styles.recurringSample}>
            <Text style={styles.recurringSampleText}>
                {DateTime.fromJSDate(date).toLocaleString(DateTime.DATETIME_MED)}
            </Text>
        </View>
    ));

    return (
        <Animated.View style={[animationStyle, styles.container]}>
            <InputSection
                title="Recurrence Pattern" 
                description="Choose how often this event repeats"
            >
                {/* Quick Presets */}
                {isNative ? (
                    <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={styles.presetsContainer}
                        keyboardShouldPersistTaps="handled"
                    >
                        {presetButtons}
                    </ScrollView>
                ) : (
                    <View style={styles.presetsWrapContainer}>
                        {presetButtons}
                    </View>
                )}

                {/* Custom Settings */}
                <View style={styles.customContainer}>
                    <View style={styles.inputRow}>
                            <View style={styles.intervalInputContainer}>
                                <Input
                                    title="Every"
                                    type="number-pad"
                                    value={String(recurrenceSettings.interval)}
                                    onUpdate={(val) => handleSettingChange('interval', parseInt(val) || 1)}
                                />
                            </View>
                            <View style={styles.frequencyInputContainer}>
                                <SelectInput
                                    title="Frequency"
                                    value={recurrenceSettings.frequency}
                                    options={FREQUENCY_OPTIONS}
                                    onUpdate={(val) => handleSettingChange('frequency', val)}
                                />
                            </View>
                        </View>

                        {/* Weekly: Day picker */}
                        {recurrenceSettings.frequency === 'Weekly' && (
                            <ToggleInput
                                title="On these days"
                                items={WEEKDAY_LABELS.map(day => ({ label: day.slice(0, 3), value: day }))}
                                selectedValues={recurrenceSettings.weekdays}
                                onItemChanged={toggleWeekday}
                                itemsPerRow={7}
                            />
                        )}

                        {/* Monthly: Date or Weekday */}
                        {recurrenceSettings.frequency === 'Monthly' && (
                            <>
                                <ToggleInput
                                    selectedValues={[recurrenceSettings.monthlyType === 'weekday' ? 'By Weekday' : 'By Date']}
                                    items={['By Date', 'By Weekday']}
                                    onItemChanged={(val) => handleSettingChange('monthlyType', val === 'By Weekday' ? 'weekday' : 'date')}
                                />

                                {recurrenceSettings.monthlyType === 'date' ? (
                                    <ToggleInput
                                        title="On these days of the month"
                                        items={DAY_OPTIONS}
                                        selectedValues={recurrenceSettings.monthDays}
                                        onItemChanged={toggleMonthDay}
                                        itemsPerRow={7}
                                    />
                                ) : (
                                    <View style={styles.inputRow}>
                                        <View style={styles.halfWidth}>
                                            <SelectInput
                                                title="Week"
                                                value={recurrenceSettings.weekPosition}
                                                options={WEEK_POSITIONS}
                                                onUpdate={(val) => handleSettingChange('weekPosition', val)}
                                            />
                                        </View>
                                        <View style={styles.halfWidth}>
                                            <SelectInput
                                                title="Day"
                                                value={recurrenceSettings.weekday}
                                                options={WEEKDAY_LABELS}
                                                onUpdate={(val) => handleSettingChange('weekday', val)}
                                            />
                                        </View>
                                    </View>
                                )}
                            </>
                        )}

                        {/* Yearly: Month picker + day options */}
                        {recurrenceSettings.frequency === 'Yearly' && (
                            <>
                                <ToggleInput
                                    title="In these months"
                                    items={MONTH_OPTIONS}
                                    selectedValues={recurrenceSettings.months}
                                    onItemChanged={toggleMonth}
                                    itemsPerRow={4}
                                />

                                <ToggleInput
                                    selectedValues={[recurrenceSettings.monthlyType === 'weekday' ? 'By Weekday' : 'By Date']}
                                    items={['By Date', 'By Weekday']}
                                    onItemChanged={(val) => handleSettingChange('monthlyType', val === 'By Weekday' ? 'weekday' : 'date')}
                                />

                                {recurrenceSettings.monthlyType === 'date' ? (
                                    <ToggleInput
                                        title="On these days"
                                        items={DAY_OPTIONS}
                                        selectedValues={recurrenceSettings.monthDays}
                                        onItemChanged={toggleMonthDay}
                                        itemsPerRow={7}
                                    />
                                ) : (
                                    <View style={styles.inputRow}>
                                        <View style={styles.halfWidth}>
                                            <SelectInput
                                                title="Week"
                                                value={recurrenceSettings.weekPosition}
                                                options={WEEK_POSITIONS}
                                                onUpdate={(val) => handleSettingChange('weekPosition', val)}
                                            />
                                        </View>
                                        <View style={styles.halfWidth}>
                                            <SelectInput
                                                title="Day"
                                                value={recurrenceSettings.weekday}
                                                options={WEEKDAY_LABELS}
                                                onUpdate={(val) => handleSettingChange('weekday', val)}
                                            />
                                        </View>
                                    </View>
                                )}
                            </>
                        )}
                    </View>

                {/* End Date */}
                <DatePickerInput
                    title="Repeat Until"
                    mode="date"
                    minimumDate={new Date()}
                    value={recurrenceSettings.endDate}
                    emptyText="Forever"
                    onUpdate={(val) => handleSettingChange('endDate', val)}
                />

                {/* Human-readable summary */}
                {recurrenceDescription && (
                    <Text style={styles.summaryText}>
                        {recurrenceDescription}
                    </Text>
                )}

                {/* Preview dates */}
                {upcomingProjections.length > 0 && (
                    <View style={styles.projectionsContainer}>
                        <Text style={styles.projectionsTitle}>Next {upcomingProjections.length} Occurrences</Text>
                        <Text style={styles.projectionsSubtitle}>Based on current recurrence settings</Text>
                        {projectionItems}
                    </View>
                )}
            </InputSection>
        </Animated.View>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: {
        width: '100%',
        overflow: 'hidden'
    },
    presetsContainer: {
        flexDirection: 'row',
        gap: 8,
        paddingVertical: 4,
    },
    presetsWrapContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        paddingVertical: 4,
    },
    presetButton: {
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: 20,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        _web: {
            transition: 'background-color 0.2s ease-in-out',
            _hover: {
                backgroundColor: colors.border,
                borderColor: colors.border,
            }
        }
    },
    presetButtonSelected: {
        backgroundColor: colors.primary,
        borderColor: colors.primary,
        _web: {
            transition: 'background-color 0.2s ease-in-out',
            _hover: {
                backgroundColor: colors.primary,
                borderColor: colors.primary,
            }
        }
    },
    presetButtonText: {
        fontFamily: fonts.body,
        fontSize: 13,
        color: colors.text,
    },
    presetButtonTextSelected: {
        color: colors.onPrimary,
        fontFamily: fonts.heading,
    },
    customContainer: {
        marginTop: 10
    },
    inputRow: {
        flexDirection: 'row',
        width: '100%',
        alignItems: 'center',
        gap: 10
    },
    intervalInputContainer: {
        flex: 1
    },
    frequencyInputContainer: {
        flex: 2
    },
    halfWidth: {
        flex: 1
    },
    summaryText: {
        fontStyle: 'italic',
        textAlign: 'center',
        color: colors.secondary,
        marginTop: 10,
        textTransform: 'capitalize'
    },
    projectionsContainer: {
        marginTop: 20,
        paddingTop: 20,
        borderTopWidth: 1,
        borderTopColor: colors.border
    },
    projectionsTitle: {
        fontSize: 18,
        fontFamily: fonts.heading,
        color: colors.text,
        paddingBottom: 5
    },
    projectionsSubtitle: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        paddingBottom: 5
    },
    recurringSample: {
        marginVertical: 5
    },
    recurringSampleText: {
        fontSize: 14,
        fontFamily: fonts.heading,
        color: colors.secondary
    }
}));

export default memo(RecurrenceSettingsNew);
